// core
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

// services
import { LoggerService, AnalyticsService } from '@campfire-nx/api';

// resources
import { IntercomApiService } from '../resources/intercom-api.service';

declare global {
  interface Window {
    Intercom: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class IntercomService {
  clickEventListener?: () => void;
  intercomRegions = {
    'eu': 'https://api-iam.eu.intercom.io',
    'au': 'https://api-iam.au.intercom.io',
    'us': 'https://api-iam.intercom.io'
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private intercomApiService: IntercomApiService,
    private loggerService: LoggerService,
    private analyticsService: AnalyticsService
  ) {
    this.initializeListener();
  }

  private initializeListener(): void {
    this.clickEventListener = () => {
      this.document.body.addEventListener('click', this.handleBodyClick);
    };
    this.clickEventListener();
  }

  private handleBodyClick = (event: Event): void => {
    if ((event.target as HTMLElement).classList.contains('intercom-launcher')) {
      event.preventDefault();
      this.show();
    }
  };

  public show() {
    if (typeof window.Intercom === 'function') {
      this.analyticsService.trackEvent(
        'show-chat',
        'general',
        'intercom',
        {}
      );
      window.Intercom('show');
    }
  }

  // This sets a callback function to run every time an agents answers, & hence user has new message
  public setOnUnreadCountChange(callback: (data: number) => void) {
    if (typeof window.Intercom === 'function') {
      window.Intercom('onUnreadCountChange', callback);
    }
  }

  public addIntercomScripts(intercomData: any, userDetail?: any) {
    let intercom = intercomData?.intercomAppId;
    let intercomRegion:keyof typeof this.intercomRegions = intercomData?.region;
    let intercomApiBase = intercomRegion ? this.intercomRegions[intercomRegion] : this.intercomRegions.us;
    let intercomSettings = this.document.getElementById('intercom-settings');
    let intercomScript = this.document.getElementById('intercom-script');
    let isIntercomSetWithoutUserDetail = intercomSettings && intercomSettings.textContent && intercomSettings.textContent.indexOf('user_id') === -1;
    let hmac!: string;
    const hours = 12;
    const millisecondsPerHour = 60 * 60 * 1000;
    let sessionDuration = hours * millisecondsPerHour;

    // Load Intercom script
    if (
      intercom &&
      (
        !intercomSettings ||
        (userDetail && isIntercomSetWithoutUserDetail)
      )
    ) {
      if (userDetail) {
        this.intercomApiService.getHash()
          .then((response) => {
            hmac = response.hmac;
            intercomSettings?.remove();
            intercomScript?.remove();

            let settingsText = `window.intercomSettings = {
              api_base: '${intercomApiBase}',
              app_id: '${intercom}',
              user_id: '${userDetail.userSlug}',
              name: '${userDetail.name}',
              email: '${userDetail.email}',
              mobile: '${userDetail.mobile}',
              created_at: '${userDetail.createdAt}',
              user_hash: '${response.hmac}',
              hide_default_launcher: true,
              session_duration: ${sessionDuration}
            }`;

            let updateObj = {
              user_id: userDetail.userSlug,
              name: userDetail.name,
              email: userDetail.email,
              mobile: userDetail.mobile,
              created_at: userDetail.createdAt,
              user_hash: response.hmac,
            };

            this.addIntercomTags(intercom, settingsText, updateObj);
          })
          .catch((error) => {
            this.loggerService.error('IntercomService', 'addIntercomScripts', { error: error }, 'addIntercomAuthenticatedUser');
          })
      } else {
        let settingsText = `window.intercomSettings = {
          api_base: '${intercomApiBase}',
          app_id: '${intercom}',
          hide_default_launcher: true,
          session_duration: ${sessionDuration}
        }`;
        this.addIntercomTags(intercom, settingsText);
      }
    }
  }

  private addIntercomTags(intercom: string, settingsText: string, updateObj?: any) {
    let intercomSettingsTag = this.document.createElement('script');
    intercomSettingsTag.id = 'intercom-settings';
    intercomSettingsTag.text = settingsText;
    this.document.body.appendChild(intercomSettingsTag);

    // Calling Intercom update after intercomsettings with hmac will create a user in intercom
    if (updateObj) {
      window.Intercom('update', updateObj);
    }

    let intercomTag = this.document.createElement('script');
    intercomTag.id = 'intercom-script';
    intercomTag.text = `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${intercom}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`;
    this.document.body.appendChild(intercomTag);

    window.Intercom('hide');
  }

  public shutdown() {
    if (typeof window.Intercom === 'function') {
      window.Intercom('shutdown');
      this.analyticsService.trackEvent(
        'shutdown-chat',
        'general',
        'intercom',
        {}
      );
    }
  }

  ngOnDestroy(): void {
    if (this.clickEventListener) {
      document.body.removeEventListener('click', this.handleBodyClick);
    }
  }
}
